import { useEffect, useState } from 'react';
import { ApiCallError } from '../api/client';
import { sysApi, type SysParent, type SysChild } from '../api/sysadmin';

interface ResetTarget {
  type: 'parent' | 'child';
  id: number;
  label: string;
}

export default function SysAdminUsersPage() {
  const [parents, setParents] = useState<SysParent[]>([]);
  const [children, setChildren] = useState<SysChild[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reset, setReset] = useState<ResetTarget | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await sysApi.listUsers();
      setParents(r.parents);
      setChildren(r.children);
    } catch (e) {
      setErr(e instanceof ApiCallError ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function submitReset() {
    if (!reset) return;
    if (newPassword.length < 4) { setErr('新密码至少 4 位'); return; }
    setErr(null);
    setResetBusy(true);
    try {
      await sysApi.resetPassword(reset.type, reset.id, newPassword);
      setToast(`已重置：${reset.label}`);
      setReset(null);
      setNewPassword('');
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setErr(e instanceof ApiCallError ? e.message : '重置失败');
    } finally {
      setResetBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title">👥 用户管理</div>
        <div className="page-subtitle">家长 {parents.length} / 孩子 {children.length}</div>
      </div>

      {err && <div className="error-box">{err}</div>}
      {toast && <div className="success-box pop-in">{toast}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <span className="spinner spinner-lg" />
        </div>
      ) : (
        <>
          <div className="card">
            <div style={{ fontWeight: 800, marginBottom: 8 }}>家长账号 ({parents.length})</div>
            {parents.length === 0 && <div className="muted">还没有家长账号</div>}
            {parents.map(p => (
              <div
                key={p.id}
                style={{
                  padding: 12, borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
                  opacity: p.deleted_at ? 0.5 : 1,
                }}
              >
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 700 }}>
                    {p.email}
                    {p.deleted_at != null && <span className="tag" style={{ marginLeft: 8, background: '#fee2e2', color: '#991b1b' }}>已删除</span>}
                    {!p.email_verified && <span className="tag" style={{ marginLeft: 8, background: '#fef3c7', color: '#78350f' }}>未验证</span>}
                    {!p.has_password && <span className="tag" style={{ marginLeft: 8, background: '#e0e7ff', color: '#3730a3' }}>无密码</span>}
                  </div>
                  <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                    ID #{p.id}
                    {p.display_name && ` · ${p.display_name}`}
                    {' · 注册 '}{fmtDate(p.created_at)}
                    {p.last_login != null && ` · 上次登录 ${fmtDate(p.last_login)}`}
                  </div>
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={() => { setReset({ type: 'parent', id: p.id, label: p.email }); setNewPassword(''); }}
                >
                  🔑 重置密码
                </button>
              </div>
            ))}
          </div>

          <div className="card">
            <div style={{ fontWeight: 800, marginBottom: 8 }}>孩子账号 ({children.length})</div>
            {children.length === 0 && <div className="muted">还没有孩子账号</div>}
            {children.map(c => (
              <div
                key={c.id}
                style={{
                  padding: 12, borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
                  opacity: c.deleted_at ? 0.5 : 1,
                }}
              >
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 700 }}>
                    {c.avatar || '👶'} {c.name}
                    {c.deleted_at != null && <span className="tag" style={{ marginLeft: 8, background: '#fee2e2', color: '#991b1b' }}>已删除</span>}
                  </div>
                  <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                    ID #{c.id}
                    {c.birth_date && ` · ${c.birth_date}`}
                    {c.parent_email ? ` · 家长 ${c.parent_email}` : ' · 未关联家长'}
                    {' · 注册 '}{fmtDate(c.created_at)}
                    {c.last_login != null && ` · 上次登录 ${fmtDate(c.last_login)}`}
                  </div>
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={() => { setReset({ type: 'child', id: c.id, label: c.name }); setNewPassword(''); }}
                >
                  🔑 重置密码
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {reset && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }}
          onClick={() => !resetBusy && setReset(null)}
        >
          <div
            className="card pop-in"
            style={{
              maxWidth: 420, width: '100%', margin: 0,
              borderRadius: '24px 24px 0 0', padding: 24,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>
              重置密码：{reset.label}
            </div>
            <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
              重置后，该账号已登录的设备会被强制登出。
            </div>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: 6 }}>新密码</label>
            <input
              type="text"
              autoFocus
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="至少 4 位"
              onKeyDown={e => e.key === 'Enter' && submitReset()}
            />
            <div className="flex" style={{ marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setReset(null)} disabled={resetBusy}>
                取消
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1 }}
                onClick={submitReset}
                disabled={resetBusy}
              >
                {resetBusy ? <span className="spinner" /> : '确认重置'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtDate(ms: number): string {
  return new Date(ms).toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}
